import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '../api/client'

export interface Supplier {
  id: string
  name: string
  role: string
  contactEmail: string
  contactPhone: string
  color?: string
  status: 'on_site' | 'en_route' | 'confirmed' | 'delayed' | 'unconfirmed'
  lastContact?: string
}

export interface SupplierCreate {
  name: string
  role: string
  contactEmail: string
  contactPhone: string
  color?: string
  status?: 'on_site' | 'en_route' | 'confirmed' | 'delayed' | 'unconfirmed'
}

export interface SupplierUpdate {
  name?: string
  role?: string
  contactEmail?: string
  contactPhone?: string
  color?: string
  status?: 'on_site' | 'en_route' | 'confirmed' | 'delayed' | 'unconfirmed'
}

export interface SupplierSearch {
  suppliers: Supplier[]
  total: number
  query: string
}

// Get all suppliers with optional filters
export const useSuppliers = (role?: string, status?: string, limit: number = 100) => {
  const queryKey = ['suppliers', { role, status, limit }]
  
  return useQuery<Supplier[]>({
    queryKey,
    queryFn: async () => {
      const params = new URLSearchParams()
      if (role) params.append('role', role)
      if (status) params.append('status', status)
      params.append('limit', limit.toString())
      
      const response = await apiClient.get(`/suppliers/?${params.toString()}`)
      return response.data
    },
    staleTime: 30000, // 30 seconds
  })
}

// Get a single supplier by ID
export const useSupplier = (supplierId: string) => {
  return useQuery<Supplier>({
    queryKey: ['supplier', supplierId],
    queryFn: async () => {
      const response = await apiClient.get(`/suppliers/${supplierId}`)
      return response.data
    },
    enabled: !!supplierId,
    staleTime: 30000,
  })
}

// Search suppliers
export const useSupplierSearch = (query: string, role?: string) => {
  return useQuery<SupplierSearch>({
    queryKey: ['suppliers', 'search', query, role],
    queryFn: async () => {
      const params = new URLSearchParams()
      params.append('q', query)
      if (role) params.append('role', role)
      
      const response = await apiClient.get(`/suppliers/search?${params.toString()}`)
      return response.data
    },
    enabled: query.length >= 2,
    staleTime: 10000,
  })
}

// Get unique supplier roles
export const useSupplierRoles = () => {
  return useQuery<string[]>({
    queryKey: ['suppliers', 'roles'],
    queryFn: async () => {
      const response = await apiClient.get('/suppliers/roles')
      return response.data
    },
    staleTime: 60000, // 1 minute
  })
}

// Create a new supplier
export const useCreateSupplier = () => {
  const queryClient = useQueryClient()
  
  return useMutation<Supplier, Error, SupplierCreate>({
    mutationFn: async (supplierData) => {
      const response = await apiClient.post('/suppliers/', supplierData)
      return response.data
    },
    onSuccess: () => {
      // Invalidate all supplier queries to refresh the data
      queryClient.invalidateQueries({ queryKey: ['suppliers'] })
    },
  })
}

// Update a supplier
export const useUpdateSupplier = () => {
  const queryClient = useQueryClient()
  
  return useMutation<Supplier, Error, { supplierId: string; data: SupplierUpdate }>({
    mutationFn: async ({ supplierId, data }) => {
      const response = await apiClient.put(`/suppliers/${supplierId}`, data)
      return response.data
    },
    onSuccess: (data) => {
      // Update the specific supplier cache
      queryClient.setQueryData(['supplier', data.id], data)
      // Invalidate supplier list queries
      queryClient.invalidateQueries({ queryKey: ['suppliers'] })
    },
  })
}

// Delete a supplier
export const useDeleteSupplier = () => {
  const queryClient = useQueryClient()
  
  return useMutation<void, Error, string>({
    mutationFn: async (supplierId) => {
      await apiClient.delete(`/suppliers/${supplierId}`)
    },
    onSuccess: (_, supplierId) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: ['supplier', supplierId] })
      // Invalidate supplier list queries
      queryClient.invalidateQueries({ queryKey: ['suppliers'] })
    },
  })
}

// Assign supplier to event
export const useAssignSupplierToEvent = () => {
  const queryClient = useQueryClient()
  
  return useMutation<Supplier, Error, { supplierId: string; eventId: string }>({
    mutationFn: async ({ supplierId, eventId }) => {
      const response = await apiClient.post(
        `/suppliers/${supplierId}/assign-event?event_id=${eventId}`
      )
      return response.data
    },
    onSuccess: (data) => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: ['supplier', data.id] })
      queryClient.invalidateQueries({ queryKey: ['suppliers'] })
    },
  })
}

// Get supplier events
export const useSupplierEvents = (supplierId: string) => {
  return useQuery<string[]>({
    queryKey: ['supplier', supplierId, 'events'],
    queryFn: async () => {
      const response = await apiClient.get(`/suppliers/${supplierId}/events`)
      return response.data
    },
    enabled: !!supplierId,
    staleTime: 30000,
  })
}